import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useInfiniteQuery } from "@tanstack/react-query";
import { CaretUp, CaretDown, X, ArrowSquareOut, Play } from "@phosphor-icons/react";
import { fetchShorts, formatViews } from "@/lib/api";
import { useVideoEnd, withJsApi } from "@/hooks/useAutoplayNext";
import { kickRepaint } from "@/lib/repaint";

const IFRAME_ID = "rv-shorts-feed-iframe";
const SWIPE_PX = 50;
const NAV_KEYS = new Set(["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight", "Escape", "PageDown", "PageUp"]);

const isYT = (url) => /youtube(?:-nocookie)?\.com\/embed/.test(url || "");
const embedFor = (v) => {
  const base = withJsApi(v.monetized_embed_url);
  const sep = base.includes("?") ? "&" : "?";
  return `${base}${sep}autoplay=1&playsinline=1${isYT(base) ? "&rel=0&modestbranding=1" : ""}`;
};
const ytCommand = (func) =>
  document.getElementById(IFRAME_ID)?.contentWindow?.postMessage(JSON.stringify({ event: "command", func, args: [] }), "*");

const NavButton = ({ onClick, disabled, label, testid, children }) => (
  <button type="button" onClick={onClick} disabled={disabled} aria-label={label} data-testid={testid}
    className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white backdrop-blur-md transition-colors duration-300 hover:border-amber hover:text-amber focus:outline-none focus-visible:ring-2 focus-visible:ring-amber disabled:opacity-25">
    {children}
  </button>
);

// Full-screen vertical feed: swipe / wheel / ArrowDown advances, and each short auto-plays the next when it ends.
export default function ShortsFeed() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const genre = params.get("genre") || undefined;
  const audience = params.get("audience") || undefined;
  const [index, setIndex] = useState(Number(params.get("i")) || 0);
  const touchY = useRef(null);
  const [paused, setPaused] = useState(false);
  const wheelLock = useRef(0);

  const { data, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery({
    queryKey: ["shorts-feed", genre, audience],
    queryFn: ({ pageParam = 1 }) => fetchShorts({ page: pageParam, limit: 20, genre, audience }),
    getNextPageParam: (last) => (last.page < last.total_pages ? last.page + 1 : undefined),
    initialPageParam: 1,
  });
  const items = data?.pages.flatMap((p) => p.items) || [];
  const current = items[index];

  const go = useCallback((step) => {
    setPaused(false);
    setIndex((i) => Math.max(0, Math.min(items.length - 1, i + step)));
  }, [items.length]);
  const close = useCallback(() => navigate("/shorts"), [navigate]);

  useEffect(() => {
    if (hasNextPage && index >= items.length - 3) fetchNextPage();
  }, [index, items.length, hasNextPage, fetchNextPage]);

  useVideoEnd({ videoId: current?.video_id, embedUrl: current?.monetized_embed_url, durationSec: current?.duration_sec,
    iframeId: IFRAME_ID, enabled: !!current, onEnd: () => go(1),
    onError: () => setTimeout(() => go(1), 1200) }); // removed/blocked video → skip ahead

  useEffect(() => {
    // Capture phase so TV spatial navigation never steals the feed's arrow keys.
    const onKey = (e) => {
      if (!NAV_KEYS.has(e.key)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (e.key === "Escape") close();
      else if (["ArrowDown", "ArrowRight", "PageDown"].includes(e.key)) go(1);
      else go(-1);
    };
    window.addEventListener("keydown", onKey, true);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey, true);
      document.body.style.overflow = prevOverflow;
    };
  }, [go, close]);

  const onWheel = (e) => {
    const now = Date.now();
    if (Math.abs(e.deltaY) < 30 || now - wheelLock.current < 700) return;
    wheelLock.current = now;
    go(e.deltaY > 0 ? 1 : -1);
  };
  const onTouchEnd = (e) => {
    if (touchY.current == null) return;
    const dy = touchY.current - e.changedTouches[0].clientY;
    touchY.current = null;
    if (Math.abs(dy) > SWIPE_PX) go(dy > 0 ? 1 : -1);
    else if (e.target.dataset.gesture) togglePlay();
  };
  const togglePlay = () => {
    if (!isYT(current?.monetized_embed_url)) return;
    ytCommand(paused ? "playVideo" : "pauseVideo");
    setPaused((p) => !p);
  };

  return (
    <div data-testid="shorts-feed" onWheel={onWheel} onTouchStart={(e) => { touchY.current = e.touches[0].clientY; }} onTouchEnd={onTouchEnd}
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black" style={{ overscrollBehavior: "none" }}>
      <Helmet><title>{current ? `${current.title} · ReelVault Shorts` : "Shorts Feed · ReelVault"}</title></Helmet>

      <div className="relative h-[100dvh] w-full sm:w-[min(100vw,calc(100dvh*9/16))] bg-[#0B0B0B]">
        {isLoading && <div className="absolute inset-0 animate-pulse bg-white/5" />}
        {!isLoading && !current && <p data-testid="shorts-feed-empty" className="absolute inset-0 flex items-center justify-center font-mono text-sm text-white/50">No shorts here yet.</p>}
        {current && (
          <div key={current.video_id} className="absolute inset-0 rv-fade">
            <iframe id={IFRAME_ID} data-testid="shorts-feed-iframe" src={embedFor(current)} title={current.title}
              allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen onLoad={(e) => kickRepaint(e.currentTarget)}
              className="rv-gpu absolute inset-0 h-full w-full border-0" />
            {/* Gesture layer: iframes swallow wheel/touch, so swipes + taps are read here (YouTube) */}
            {isYT(current.monetized_embed_url) && (
              <button type="button" data-gesture="1" data-testid="shorts-feed-gesture" aria-label={paused ? "Play" : "Pause"} tabIndex={-1}
                onClick={(e) => { if (e.detail) togglePlay(); }} onTouchEnd={(e) => e.preventDefault()}
                className="absolute inset-x-0 top-0 bottom-24 z-10 cursor-pointer bg-transparent">
                {paused && <span data-testid="shorts-feed-paused" className="absolute inset-0 flex items-center justify-center"><Play size={64} weight="fill" className="text-white/80 drop-shadow-lg" /></span>}
              </button>
            )}
          </div>
        )}
        {current && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent p-5 pb-8">
            <p data-testid="shorts-feed-counter" className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber">Short {index + 1} / {data?.pages[0]?.total || items.length}</p>
            <h1 data-testid="shorts-feed-title" className="mt-2 line-clamp-2 font-serif text-xl text-white">{current.title}</h1>
            <p className="mt-1 font-mono text-[11px] text-white/50">{formatViews(current.views)} views</p>
          </div>
        )}
      </div>

      <div className="absolute right-4 top-4 flex gap-2">
        {current && (
          <Link to={`/watch/${current.video_id}`} data-testid="shorts-feed-open" aria-label="Open full page"
            className="flex h-12 w-12 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white backdrop-blur-md transition-colors duration-300 hover:border-amber hover:text-amber">
            <ArrowSquareOut size={20} />
          </Link>
        )}
        <NavButton onClick={close} label="Close feed" testid="shorts-feed-close"><X size={20} /></NavButton>
      </div>
      <div className="absolute right-4 top-1/2 flex -translate-y-1/2 flex-col gap-3">
        <NavButton onClick={() => go(-1)} disabled={index === 0} label="Previous short" testid="shorts-feed-prev"><CaretUp size={22} weight="bold" /></NavButton>
        <NavButton onClick={() => go(1)} disabled={index >= items.length - 1} label="Next short" testid="shorts-feed-next"><CaretDown size={22} weight="bold" /></NavButton>
      </div>
    </div>
  );
}
